// src/encounter/lib/assign-care-plan-text.ts
// [PROD-3206] The words of the "Assign care plan" dialog. Pure. The error sentences follow pm-react's
// `describeEnrolmentError` (studio/services/care-model-enrolments-api.ts), shortened: a lost answer or a 5xx is
// "not confirmed", never "not assigned", because the server may have acted. The server's own text is shown ONLY for a
// 422 (the reasons a care model cannot start); every other status gets the app's sentence alone. UI text is US English.
import { ApiError } from '@/shared/api/api-client';
import { formatReadoutDate } from './care-plan-readout-text';

/**
 * "Already on {care model} since {date}." The C2 reply names the first enroller by USER ID only (Workflows #178,
 * `enrolledBy`), not by name, so no name is shown. A missing or unreadable date leaves the date out.
 */
export function alreadyOnLine(modelName: string, enrolledOn: string | null | undefined): string {
  const readable = enrolledOn && !Number.isNaN(new Date(enrolledOn).getTime());
  return readable ? `Already on ${modelName} since ${formatReadoutDate(enrolledOn)}.` : `Already on ${modelName}.`;
}

/** A 422's reasons: the `errors[].value` texts, else the ProblemDetails / ResponseModel text. Read for 422 only. */
function serverDetail(body: string): string | null {
  const text = body.trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as { errors?: unknown; detail?: unknown; title?: unknown; message?: unknown };
    if (Array.isArray(parsed.errors)) {
      const reasons = (parsed.errors as Array<{ key?: unknown; value?: unknown } | null>)
        .filter((e) => !(typeof e?.key === 'string' && e.key.startsWith('_')))
        .map((e) => (typeof e?.value === 'string' ? e.value.trim() : ''))
        .filter(Boolean);
      if (reasons.length) return [...new Set(reasons)].join(' ');
    }
    for (const v of [parsed.detail, parsed.title, parsed.message]) if (typeof v === 'string' && v.trim()) return v.trim();
    return null;
  } catch {
    return text.startsWith('<') || text.length > 300 ? null : text;
  }
}

const NOT_CONFIRMED = 'so this is not confirmed. Check the patient’s care plan before you try again.';

/**
 * 409: C2 refuses an overlapping run (the patient already has an active enrolment in this model). The same
 * "already on" style as `alreadyOnLine`, with no name and no date (a 409 carries neither).
 */
export function alreadyActiveLine(modelName: string): string {
  return `Already on ${modelName}: this patient already has an active care plan from it. No new care plan was created.`;
}

/** A failed assign, as a sentence the physician can act on. `modelName` names the care model for a 409. */
export function describeAssignError(err: unknown, modelName: string): string {
  if (!(err instanceof ApiError)) {
    // `EnrolmentUnconfirmedError` (care-model-api.ts): a 2xx that named no enrolment. Anything else here is a
    // transport failure (fetch rejects with no status). Both may follow a server that acted.
    return err instanceof Error && err.name === 'EnrolmentUnconfirmedError'
      ? `The workflow service answered without an enrollment, ${NOT_CONFIRMED}`
      : `The workflow service could not be reached, ${NOT_CONFIRMED}`;
  }
  if (err.status === 0) return `The workflow service could not be reached, ${NOT_CONFIRMED}`;
  if (err.status === 401) return 'Your session has expired. Sign in again, then try again.';
  if (err.status === 403) return 'The workflow service refused this sign-in. Nothing was changed.';
  if (err.status === 404) {
    return 'Not found in your organization: the care model may not be published, or the patient may not be available to you.';
  }
  if (err.status === 409) return alreadyActiveLine(modelName);
  if (err.status === 422) {
    const lead = 'This care model cannot start as it is saved. Nothing was changed.';
    const reasons = serverDetail(err.body);
    return reasons ? `${lead} The server said: ${reasons}` : lead;
  }
  if (err.status >= 500) return `The workflow service failed, ${NOT_CONFIRMED}`;
  return 'The workflow service did not accept this request. Nothing was changed.';
}
